import './ContactsTableController.scss'
import {ToastNotificationServiceName} from "../../../utils/names/serviceNames";
import {LoaderStatusValue} from "../../../loader/service/loaderStatus";
import {IController, IPromise, IScope} from "angular";
import {Pageable} from "../../../utils/models/Page";
import {Sorting, SortingDirection, SortingDirectionFieldNames} from "../../../dynamic-ordering/components/sorting";
import {
    SendgridContactListDialogComponent
} from "../../../customers/component/sendgrid-contact-dialog/SendgridContactList.dialog.component";
import {SendGridContactListOptions} from "../../../customers/model/SendGridContactListOptions";
import {ToastNotificationService} from "../../../utils/ToastNotificationService";
import {Contact} from "../../model/Contact";
import {ContactFilter} from "../../model/ContactFilter";
import {IContactService} from "../../service/IContactService";

interface Scope<T extends Contact> extends IScope {
    tableTitle: string;
    contacts: T[];
    selectedContactIds: number[];
    pagingConfig: Pageable;
    filter: ContactFilter;
    updateContacts: () => IPromise<void>;
    navigateTo: (id: number) => void;
    showContactsSendGrid: boolean;
    showPartnerFields: boolean;
    createSendgridContactGroup: (e) => any;
    createSendgridContactGroupForAll: (e) => any;
}

export abstract class ContactsTableController<T extends Contact> implements IController {

    static instanceFactory = (
        contactServiceName: string,
        controllerClass: any
    ) => ({
        controller: [
            '$scope',
            '$location',
            '$mdDialog',
            'LoaderStatusValue',
            ToastNotificationServiceName,
            contactServiceName,
            controllerClass
        ],
        template: require('./ContactsTableController.html'),
        bindings: {
            initialFilter: '<',
            onContactProfileClick: '<'
        }
    });

    protected initialFilter?: Partial<ContactFilter>;
    protected onContactProfileClick?: (id: number) => any;

    constructor(private $scope: Scope<T>,
                private $location: ng.ILocationService,
                private $mdDialog: angular.material.IDialogService,
                private loader: LoaderStatusValue,
                private notificationService: ToastNotificationService,
                private contactService: IContactService<T>) {
        this.$scope.contacts = [];
        this.$scope.selectedContactIds = [];
        this.$scope.showContactsSendGrid = false;
        this.$scope.createSendgridContactGroup = this.createSendgridContactGroup;
        this.$scope.createSendgridContactGroupForAll = this.createSendgridContactGroupForAll;
        this.$scope.$watch('selectedContactIds.length', (newValue: number) => {
            $scope.showContactsSendGrid = newValue > 0;
        });
        this.$scope.pagingConfig = {
            page: 1,
            pageSize: 20,
            fetchPage: this.updateContacts,
            totalElements: 0,
        }
        this.$scope.filter = {
            employeeIds: [],
            includeActivatedEmployeeOnly: false,
            includeDeleted: false,
            nameFilter: "",
            orderBy: [
                new Sorting(SortingDirection.DESC, SortingDirectionFieldNames.LOAN_OFFICER)
            ],
            selectedBranchesIds: [],
            selectedBrandsNames: []
        }
        this.$scope.updateContacts = this.updateContacts;
        this.$scope.navigateTo = (id) => {
            if (this.onContactProfileClick) {
                this.onContactProfileClick(id);
            }
            this.$location.url(this.profileUrl(id))
        }
        this.$scope.tableTitle = this.title();
        this.$scope.showPartnerFields = this.isPartnerTable();
    }

    $onInit() {
        this.$scope.filter = Object.assign(this.$scope.filter, this.initialFilter);
        this.updateContacts();
    }

    private updateContacts = (): IPromise<void> => {
        this.loader.on();
        return this.contactService.fetchContacts(this.$scope.filter, this.$scope.pagingConfig)
            .then(resp => {
                if (resp.status === 200) {
                    this.$scope.contacts = resp.data.content;
                    this.$scope.pagingConfig.totalElements = resp.data.totalElements;
                }
            })
            .then(() => this.loader.off());
    };

    createSendgridContactGroupForAll = ($event: MouseEvent) => {
        this.$mdDialog.show(SendgridContactListDialogComponent.instance($event))
            .then((sendGridOptions: SendGridContactListOptions) => {
                this.loader.on();
                return this.createSendgridGroupFromSelectedFilter(sendGridOptions);
            })
    };

    createSendgridContactGroup = ($event: MouseEvent) => {
        this.$mdDialog.show(SendgridContactListDialogComponent.instance($event))
            .then((sendGridOptions: SendGridContactListOptions) => {
                this.loader.on();
                this.createSendgridGroupFromSelectedCustomersIds(sendGridOptions);
                this.$scope.updateContacts();
            })
    };

    private isPartnerTable = (): boolean => {
        return this.$scope.tableTitle == 'Partners';
    }


    private allContactsSelected() {
        return this.$scope.selectedContactIds.length == this.$scope.pagingConfig.pageSize;
    }

    private createSendgridGroupFromSelectedFilter = (sendGridOptions: SendGridContactListOptions): IPromise<void> => {
        let filter = this.$scope.filter;
        return this.contactService.createSendgridContactsListFromFilteredContacts(sendGridOptions, filter)
            .then(this.handleNewSendgridGroupSuccess, this.handleNewSendgridGroupFailure);
    };

    private createSendgridGroupFromSelectedCustomersIds = (sendGridOptions: SendGridContactListOptions): IPromise<void> => {
        let selectedContactIds = this.$scope.selectedContactIds;
        return this.contactService.createSendgridContactsListFromContactIds(sendGridOptions, selectedContactIds)
            .then(this.handleNewSendgridGroupSuccess, this.handleNewSendgridGroupFailure);
    };


    private handleNewSendgridGroupSuccess = resp => {
        this.loader.off();
        this.notificationService.generateSuccessMessage("Successfully created sendgrid contact group!");
    };

    private handleNewSendgridGroupFailure = err => {
        this.loader.off();
        this.notificationService.generateErrorMessage("Failed to create sendgrid contact group!");
    };

    protected abstract title(): string;

    protected abstract profileUrl(id: number): string;

}